import React from 'react';
import { AutoComplete, Col, Form, Input, Row } from 'antd';
import type { FormInstance } from 'antd';
import { CarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TransportFormSection from './TransportFormSection';
import TransportStatusPicker from './TransportStatusPicker';
import type { SharedTransportFormValues } from './sharedTransportFormUtils';

interface CustomerOption {
  name: string;
}

interface TransportCompanyOption {
  id: string;
  name: string;
  nip?: string | null;
}

interface TransportCarrierSectionProps {
  form: FormInstance<SharedTransportFormValues>;
  customerOptions: CustomerOption[];
  transportCompanyOptions: TransportCompanyOption[];
  onCustomerTouched: () => void;
}

const TransportCarrierSection: React.FC<TransportCarrierSectionProps> = ({
  form,
  customerOptions,
  transportCompanyOptions,
  onCustomerTouched,
}) => {
  const { t } = useTranslation(['transport']);
  const transportCompany = Form.useWatch('transportCompany', form);
  const summary = String(transportCompany ?? '').trim() || undefined;

  return (
    <TransportFormSection sectionKey="carrier" step={1} icon={<CarOutlined />} label={t('transport:sections.carrier')} summary={summary}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <Form.Item
            name="transportCompany"
            label={t('transport:fields.transportCompany')}
            rules={[{
              required: true,
              whitespace: true,
              message: t('transport:validation.companyRequired'),
            }]}
          >
            <AutoComplete
              allowClear
              placeholder={t('transport:placeholders.transportCompany')}
              options={transportCompanyOptions.map((company) => ({
                value: company.name,
                label: company.nip ? `${company.name} · NIP ${company.nip}` : company.name,
              }))}
              filterOption={(input, option) => {
                const needle = input.trim().toLowerCase();
                if (!needle) return true;
                const optValue = String(option?.value ?? '').toLowerCase();
                const optLabel = String(option?.label ?? '').toLowerCase();
                return optValue.includes(needle) || optLabel.includes(needle);
              }}
              onSelect={(value) => {
                const picked = transportCompanyOptions.find(
                  (company) => company.name.toLowerCase() === String(value).toLowerCase(),
                );
                form.setFieldsValue({ transportCompanyId: picked?.id ?? null });
              }}
              onChange={(value) => {
                // Clear the hidden id whenever the typed text no longer
                // matches a registered carrier exactly. Without this, picking
                // "Acme" (id=X) and then editing the text to "Acmee" would
                // keep the stale id in the payload. The backend would then
                // mirror Acme's name back onto a row the user thought was a
                // new carrier, silently discarding their edit.
                const normalized = String(value ?? '').trim().toLowerCase();
                const matched = normalized
                  ? transportCompanyOptions.find(
                      (company) => company.name.toLowerCase() === normalized,
                    )
                  : null;
                form.setFieldsValue({ transportCompanyId: matched?.id ?? null });
              }}
            />
          </Form.Item>
          {/* Hidden-but-registered field: the Form.Item owns the value so
              setFieldsValue + onFinish both see it. A plain <Input /> (not
              type="hidden") keeps Ant's controlled-input contract intact. */}
          <Form.Item name="transportCompanyId" hidden noStyle>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Form.Item
            name="transportContact"
            label={t('transport:fields.transportContact')}
          >
            <Input placeholder={t('transport:placeholders.transportContact')} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Form.Item
            name="customer"
            label={t('transport:fields.customer')}
          >
            <AutoComplete
              allowClear
              placeholder={t('transport:placeholders.customer')}
              options={customerOptions.map((customer) => ({
                value: customer.name,
                label: customer.name,
              }))}
              filterOption={(input, option) =>
                String(option?.value ?? '').toLowerCase().includes(input.trim().toLowerCase())
              }
              // Any manual edit (typing, clearing, or picking) stops the
              // create-mode auto-fill from the selected orders.
              onChange={onCustomerTouched}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Form.Item
            name="status"
            label={t('transport:fields.status')}
            rules={[{ required: true, message: t('transport:validation.statusRequired') }]}
          >
            <TransportStatusPicker />
          </Form.Item>
        </Col>
      </Row>
    </TransportFormSection>
  );
};

export default TransportCarrierSection;
